
import get from 'lodash/get'
import { mapGetters } from 'vuex'

import { fetchRatingCategoriesByProduct, fetchSEOTags } from '@http/endpoints'
import { ROUTES } from '@router'

import { RATE_RANGES } from '../RateFilter/RateFilter.constants'

import translations from './ConnectedPage.translations'
import Page from './Page.vue'
import { REVIEWS_LIST_ID } from './constants'
import getConfig from './store/getConfig'

export default {
  components: {
    Page,
  },
  layout: 'empty',
  async asyncData({ store, route, error }) {
    const { params, name, query, fullPath, path } = route

    const routeInfo = {
      name,
      productId: get(params, 'productId'),
      landingId: get(params, 'landingId'),
      sellerId: get(params, 'sellerId'),
      landingSlug: get(params, 'landingSlug'),
      sellerSlug: get(params, 'sellerSlug'),
      query,
      fullPath,
      path,
    }

    try {
      await store.dispatch('reviewpage/fetch', { routeInfo })

      if (routeInfo.name === ROUTES.REVIEWS.PAGES.PRODUCT) {
        const { payload: ratingCategories } = await store
          .dispatch('http/request', {
            request: fetchRatingCategoriesByProduct,
            pathParams: { productId: routeInfo.productId },
            // [COM-362]] TODO to remove withReviewTranslation when it is done on BE side
            queryParams: { withReviewTranslation: true },
          })
          .catch(() => ({ payload: [] }))

        return {
          routeInfo,
          ratingCategories,
        }
      }
    } catch (err) {
      error({
        statusCode: err.status_code,
        message: err.message,
      })
    }

    return {
      routeInfo,
    }
  },

  data() {
    return {
      tags: [],
      ratingCategories: [],
      routeInfo: {},
    }
  },

  head() {
    return {
      title: this.currentPageSeo && this.currentPageSeo.title,
      meta: [
        {
          hid: 'description',
          name: 'description',
          content: this.currentPageSeo && this.currentPageSeo.description,
        },
      ],
      link: [
        {
          rel: 'canonical',
          href: `${this.baseUrl}${this.routeInfo.fullPath}`,
        },
      ],
    }
  },
  computed: {
    ...mapGetters({
      breakpoint: 'breakpoint',
      breadcrumb: 'reviewpage/breadCrumb/get',
      isMaxReviewsReached: 'reviews/list/isMaxReviewsReached',
      reviews: 'reviews/list/get',
      summary: 'reviews/summary/get',
      baseUrl: 'config/baseURL',
    }),
    pageType() {
      return this.routeInfo.name
    },
    currentPageSeo() {
      return this[`${this.routeInfo.name}Seo`]
    },
    globalRate() {
      const { rateModule } = getConfig(this.routeInfo)

      return this.$store.getters[`rates/${rateModule}/get`]
    },
    selectedFilter() {
      const { rate_min: min, rate_max: max } = this.$route.query
      const range = RATE_RANGES.find(({ value }) => value === `${min}-${max}`)

      return range?.value || RATE_RANGES[0].value
    },
    pageTitle() {
      const { productId, query } = this.routeInfo

      if (productId) {
        return query.productName || this.$t(translations.productNamePlaceholder)
      }

      const slug = this.breadcrumb[1]

      return slug && slug.title
    },
    reviewsSeo() {
      return {
        description: this.$t(translations.allDescription, {
          count: this.globalRate.reviewsCount,
          rate: this.globalRate.averageRate,
        }),
        title: this.$t(translations.allTitle),
      }
    },
    merchant_reviewsSeo() {
      return {
        description: this.$t(translations.merchantDescription, {
          count: this.globalRate.reviewsCount,
          merchantName: this.pageTitle,
          rate: this.globalRate.averageRate,
        }),
        title: this.$t(translations.merchantTitle, {
          merchantName: this.pageTitle,
        }),
      }
    },
    product_reviewsSeo() {
      const { averageRate, reviewsCount } = this.globalRate

      return {
        description: this.$t(translations.productDescription, {
          count: reviewsCount,
          productName: this.pageTitle,
          rate: averageRate,
        }),
        title: this.$t(translations.productTitle, {
          productName: this.pageTitle,
        }),
      }
    },
    landing_reviewsSeo() {
      return {
        description: this.$t(translations.landingDescription, {
          count: this.globalRate.reviewsCount,
          productName: this.pageTitle,
          rate: this.globalRate.averageRate,
        }),
        title: this.$t(translations.landingTitle, {
          productName: this.pageTitle,
        }),
      }
    },
  },
  watchQuery: true,
  beforeMount() {
    if (this.routeInfo.name === ROUTES.REVIEWS.PAGES.LANDING) {
      // Don't use the `await` notation because we don't want this to be blocking.
      // Only update the tags in the components when we received them.
      this.$store
        .dispatch('http/request', {
          request: fetchSEOTags,
          queryParams: {
            model: 'landing',
            pk: this.routeInfo.landingId,
          },
        })
        .then(({ payload: tags = [] } = {}) => {
          this.tags = tags
        })
        .catch(() => {
          // Nothing to do.
        })
    }
  },
  beforeDestroy() {
    this.$store.dispatch('reviewpage/reset')
  },
  methods: {
    handleFetchMore() {
      this.$store.dispatch('reviewpage/page/increment')
      this.$store.dispatch('reviews/list/fetchMore', {
        routeInfo: this.routeInfo,
      })
    },
    handleFilter({ min, max }) {
      const target =
        this.breakpoint < 2 ? REVIEWS_LIST_ID.MOBILE : REVIEWS_LIST_ID.DESKTOP
      document.getElementById(target).scrollIntoView({
        block: 'nearest',
        behavior: 'smooth',
      })

      this.$navigation.push({
        type: 'internal',
        query: { ...this.routeInfo.query, rate_min: min, rate_max: max },
      })
    },
  },
}
